import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Typography, Box, Snackbar, Alert, InputAdornment, IconButton } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { setAuthToken } from '../utils/api';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const LoginForm = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { refreshUser } = useAuth();

    // Check for session expiration on mount
    useEffect(() => {
        const sessionExpired = sessionStorage.getItem('sessionExpired');
        if (sessionExpired === 'true') {
            setSnackbar({
                open: true,
                message: 'Your session has expired. Please log in again.',
                severity: 'warning'
            });
            sessionStorage.removeItem('sessionExpired');

            // Clear the location state to prevent redirect to previous page after timeout
            // User should start fresh from dashboard after session timeout
            if (location.state?.from) {
                window.history.replaceState({}, document.title);
            }
        }
    }, [location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post('/auth/login', { username, password });

            // Store token and user info
            localStorage.setItem('token', res.data.token);
            localStorage.setItem('userRole', res.data.user.role);
            if(res.data.user.role === 'employee') {
                localStorage.setItem('userLocation', res.data.user.location);
            }
            setAuthToken(res.data.token);

            // Refresh auth context and wait for it to complete
            await refreshUser();

            // Check if password change is required (first login)
            if (res.data.requiresPasswordChange) {
                setSnackbar({
                    open: true,
                    message: 'Password change required. Redirecting...',
                    severity: 'warning'
                });
                setTimeout(() => {
                    navigate('/change-password', { replace: true });
                }, 1000);
                return;
            }

            setSnackbar({ open: true, message: 'Login successful', severity: 'success' });

            // Small delay to ensure auth context is fully updated before navigation
            // This prevents race conditions where protected routes check auth before context updates
            setTimeout(() => {
                const intendedDestination = location.state?.from?.pathname || '/dashboard';
                navigate(intendedDestination, { replace: true });
            }, 100);
        } catch (error) {
            console.error('Login failed', error);

            // Handle account lockout (423 status code)
            if (error.response?.status === 423) {
                const lockoutMessage = error.response?.data?.msg || 'Account locked due to multiple failed login attempts.';
                setSnackbar({
                    open: true,
                    message: lockoutMessage,
                    severity: 'error'
                });
                return;
            }

            // Handle rate limit (429 status code)
            if (error.response?.status === 429) {
                const rateLimitMessage = error.response?.data?.msg || error.response?.data?.message || 'Too many login attempts. Please try again later.';
                setSnackbar({
                    open: true,
                    message: rateLimitMessage,
                    severity: 'warning'
                });
                return;
            }

            // Handle other errors (including failed login attempts with remaining attempts info)
            const errorMessage = error.response?.data?.msg || 'Login failed. Please check your credentials.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                bgcolor: '#f5f7fa',
                padding: { xs: '20px', sm: '0' },
            }}
        >
            <Box
                component="img"
                src="/Brand_logo.svg"
                alt="Brand Logo"
                sx={{
                    width: { xs: '80%', sm: '500px' },
                    height: 'auto',
                    mb: 2,
                }}
            />
            <Container
                maxWidth="xs"
                sx={{
                    mt: -6,
                    p: 4,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    width: { xs: '100%', sm: 'auto' },
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Typography
                    variant="h4"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        mb: 3
                    }}
                >
                    Login
                </Typography>

                <form onSubmit={handleSubmit}>
                    <TextField
                        label="Email or Phone Number"
                        variant="outlined"
                        fullWidth
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        margin="normal"
                        required
                        InputLabelProps={{ style: { fontSize: '1rem' } }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />

                    <TextField
                        label="Password"
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        margin="normal"
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        InputLabelProps={{ style: { fontSize: '1rem' } }}
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        type="submit"
                        sx={{
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                            '&:hover': {
                                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                            }
                        }}
                    >
                        Login
                    </Button>
                </form>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default LoginForm;
